import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Property } from '../api/properties/types';
import { useProperties } from '../api/properties/useProperties';
import { useUser } from '../api/user/useUser';

interface CurrentPropertyContextType {
  currentProperty: Property | undefined;
  setCurrentPropertyId: (id: string | undefined) => void;
}

const CurrentPropertyContext = createContext<CurrentPropertyContextType | undefined>(undefined);

export const useCurrentProperty = (): CurrentPropertyContextType => {
  const context = useContext(CurrentPropertyContext);
  if (!context) {
    throw new Error('useCurrentProperty must be used within a CurrentPropertyProvider');
  }
  return context;
};

export const CurrentPropertyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useUser();
  const [currentPropertyId, setCurrentPropertyId] = useState<string | undefined>(undefined);
  const initialized = useRef(false);

  useEffect(() => {
    if (!user) {
      initialized.current = false;
      setCurrentPropertyId(undefined);
    } else if (!initialized.current && user.properties?.[0]?.id) {
      initialized.current = true;
      setCurrentPropertyId(user.properties[0].id);
    }
  }, [user]);

  const { data: currentProperty } = useProperties(currentPropertyId);

  const value = useMemo(
    () => ({ currentProperty, setCurrentPropertyId }),
    [currentProperty, setCurrentPropertyId],
  );

  return (
    <CurrentPropertyContext.Provider value={value}>
      {children}
    </CurrentPropertyContext.Provider>
  );
};
